import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, IconButton,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tabs, Tab,
  CircularProgress, Snackbar, Alert, MenuItem, Select, FormControl, InputLabel,
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent,
  TimelineDot
} from "@mui/material";
import { Visibility, Refresh, Search, Update } from "@mui/icons-material";
import axios from "axios";

const API_BASE = "https://api.bharatdarshan.app/api/app/services/admin";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

const SECTIONS = ["all", "sanatani", "kutumb", "rojgar", "postbanao"];
const STATUSES = ["all", "pending", "active", "processing", "completed", "expired", "failed", "refunded", "cancelled"];

const getStatusColor = (status) => {
  switch (status) {
    case "active": return "success";
    case "pending": return "warning";
    case "processing": return "info";
    case "completed": return "primary";
    case "expired": return "default";
    case "failed": return "error";
    case "refunded": return "secondary";
    case "cancelled": return "error";
    default: return "default";
  }
};

const getSectionColor = (section) => {
  switch (section) {
    case "sanatani": return "#FF9800";
    case "kutumb": return "#4CAF50";
    case "rojgar": return "#2196F3";
    case "postbanao": return "#9C27B0";
    default: return "#757575";
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
};

const PurchaseHistory = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Detail Dialog
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // Update Status Dialog
  const [updateDialog, setUpdateDialog] = useState(false);
  const [updatePurchase, setUpdatePurchase] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: "", comment: "", adminNote: "" });
  const [updateLoading, setUpdateLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const currentSection = SECTIONS[tabValue];

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/purchases?page=${page + 1}&limit=${rowsPerPage}`;
      if (currentSection !== "all") url += `&section=${currentSection}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });
      if (res.data.success) {
        setPurchases(res.data.data || []);
        setTotalCount(res.data.totalCount || res.data.total || (res.data.data || []).length);
      }
    } catch (err) {
      console.error("Error fetching purchases:", err);
      setSnackbar({ open: true, message: "Failed to load purchases", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPurchases(); }, [tabValue, statusFilter, page]);

  const handleSearch = () => {
    setPage(0);
    fetchPurchases();
  };

  const handleViewDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setDetailDialog(true);
  };

  const handleOpenUpdate = (purchase) => {
    setUpdatePurchase(purchase);
    setUpdateForm({
      status: purchase.status || "pending",
      comment: "",
      adminNote: ""
    });
    setUpdateDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!updatePurchase || !updateForm.status) return;
    setUpdateLoading(true);
    try {
      await axios.put(`${API_BASE}/purchase/${updatePurchase._id}`, {
        status: updateForm.status,
        comment: updateForm.comment,
        adminNote: updateForm.adminNote
      }, { headers: getAuthHeaders() });
      setSnackbar({ open: true, message: "Status updated successfully", severity: "success" });
      setUpdateDialog(false);
      fetchPurchases();
    } catch (err) {
      console.error("Error updating status:", err);
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to update status", severity: "error" });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#FF9800" }}>Purchase History</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPurchases}>Refresh</Button>
      </Box>

      {/* Section Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); setPage(0); }}
          variant="scrollable" scrollButtons="auto"
          sx={{ "& .MuiTab-root": { textTransform: "capitalize", fontWeight: "bold" } }}>
          {SECTIONS.map((s) => (
            <Tab key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} />
          ))}
        </Tabs>
      </Paper>

      {/* Status Filter Chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {STATUSES.map((s) => (
          <Chip key={s} label={s.charAt(0).toUpperCase() + s.slice(1)}
            variant={statusFilter === s ? "filled" : "outlined"}
            color={s === "all" ? "primary" : getStatusColor(s)}
            onClick={() => { setStatusFilter(s); setPage(0); }}
            sx={{ cursor: "pointer", fontWeight: statusFilter === s ? "bold" : "normal" }} />
        ))}
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <TextField size="small" placeholder="Search by user name or mobile..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          sx={{ flex: 1 }}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "gray" }} /> }} />
        <Button variant="contained" onClick={handleSearch}
          sx={{ bgcolor: "#FF9800", "&:hover": { bgcolor: "#F57C00" } }}>Search</Button>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#FF9800" }}>
              <TableRow>
                {["User", "Mobile", "Service", "Section", "Amount", "Payment ID", "Status", "Date", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No purchases found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((purchase) => (
                  <TableRow key={purchase._id} hover sx={{ cursor: "pointer" }}
                    onClick={() => handleViewDetails(purchase)}>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {purchase.userId?.customerName || purchase.userName || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>{purchase.userId?.phone_number || purchase.userMobile || "N/A"}</TableCell>
                    <TableCell>{purchase.serviceId?.name || purchase.serviceName || "N/A"}</TableCell>
                    <TableCell>
                      <Chip label={purchase.section || purchase.serviceId?.section || "N/A"} size="small"
                        sx={{ bgcolor: getSectionColor(purchase.section || purchase.serviceId?.section), color: "white" }} />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        {"\u20B9"}{purchase.amountPaid || purchase.amount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {purchase.paymentId || purchase.razorpayPaymentId || "\u2014"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={purchase.status || "pending"} color={getStatusColor(purchase.status)} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(purchase.createdAt || purchase.purchaseDate)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton color="primary" onClick={() => handleViewDetails(purchase)}>
                        <Visibility />
                      </IconButton>
                      <IconButton color="warning" onClick={() => handleOpenUpdate(purchase)}>
                        <Update />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={totalCount} rowsPerPage={rowsPerPage}
            page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPageOptions={[50]}
          />
        </TableContainer>
      )}

      {/* Purchase Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#FF9800", color: "white" }}>Purchase Details</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedPurchase && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography><b>User:</b> {selectedPurchase.userId?.customerName || selectedPurchase.userName || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Mobile:</b> {selectedPurchase.userId?.phone_number || selectedPurchase.userMobile || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Service:</b> {selectedPurchase.serviceId?.name || selectedPurchase.serviceName || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Section:</b>{" "}
                    <Chip label={selectedPurchase.section || selectedPurchase.serviceId?.section || "N/A"} size="small"
                      sx={{ bgcolor: getSectionColor(selectedPurchase.section || selectedPurchase.serviceId?.section), color: "white" }} />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Amount Paid:</b> {"\u20B9"}{selectedPurchase.amountPaid || selectedPurchase.amount || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Payment ID:</b> {selectedPurchase.paymentId || selectedPurchase.razorpayPaymentId || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Status:</b>{" "}
                    <Chip label={selectedPurchase.status || "pending"} color={getStatusColor(selectedPurchase.status)} size="small" />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><b>Purchase Date:</b> {formatDate(selectedPurchase.createdAt || selectedPurchase.purchaseDate)}</Typography>
                </Grid>
                {selectedPurchase.startDate && (
                  <Grid item xs={6}>
                    <Typography><b>Start Date:</b> {formatDate(selectedPurchase.startDate)}</Typography>
                  </Grid>
                )}
                {selectedPurchase.endDate && (
                  <Grid item xs={6}>
                    <Typography><b>End Date:</b> {formatDate(selectedPurchase.endDate)}</Typography>
                  </Grid>
                )}
                {selectedPurchase.adminNote && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: "#FFF3E0" }}>
                      <Typography variant="subtitle2" color="textSecondary">Admin Notes:</Typography>
                      <Typography>{selectedPurchase.adminNote}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* Status History Timeline */}
              {selectedPurchase.statusHistory && selectedPurchase.statusHistory.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Status History</Typography>
                  <Paper sx={{ p: 2 }}>
                    {selectedPurchase.statusHistory.map((entry, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", mb: 2, pl: 2, borderLeft: "3px solid #FF9800" }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Chip label={entry.status} color={getStatusColor(entry.status)} size="small" />
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(entry.changedAt || entry.date)}
                            </Typography>
                          </Box>
                          {entry.comment && <Typography variant="body2">{entry.comment}</Typography>}
                          {entry.changedBy && (
                            <Typography variant="caption" color="textSecondary">
                              By: {entry.changedBy?.name || entry.adminName || "Admin"}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
          <Button variant="contained" color="warning" onClick={() => {
            setDetailDialog(false);
            if (selectedPurchase) handleOpenUpdate(selectedPurchase);
          }}>Update Status</Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateDialog} onClose={() => setUpdateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#FF9800", color: "white" }}>Update Purchase Status</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {updatePurchase && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Updating: <b>{updatePurchase.serviceId?.name || updatePurchase.serviceName || "N/A"}</b>
                {" "}for <b>{updatePurchase.userId?.customerName || updatePurchase.userName || "N/A"}</b>
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select value={updateForm.status} label="Status"
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}>
                  {STATUSES.filter(s => s !== "all").map((s) => (
                    <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth multiline rows={2} label="Comment (visible to user)" sx={{ mb: 2 }}
                value={updateForm.comment} onChange={(e) => setUpdateForm({ ...updateForm, comment: e.target.value })} />
              <TextField fullWidth multiline rows={2} label="Admin Note (internal only)"
                value={updateForm.adminNote} onChange={(e) => setUpdateForm({ ...updateForm, adminNote: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog(false)} disabled={updateLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus} disabled={updateLoading}
            sx={{ bgcolor: "#FF9800", "&:hover": { bgcolor: "#F57C00" } }}>
            {updateLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseHistory;
